import { useEffect } from 'react'
import { useUltraPlayStore } from '@stores'
import { playUltraPlaySound, winSoundForMultiplier, ULTRA_PLAY_SOUND } from './ultraPlaySound'
import { GameUIMessageType, SoundTypes } from 'game-ui-types'

// Tell the engine which of ITS sounds may play. While Ultra Play is active we restrict the engine to
// background music only, so Ultra Play's own SFX sit on top (the agreed protocol; see messageTypes).
const requestSoundConfigChange = (isGlobalEnabled: boolean, volumes?: { [key in SoundTypes]?: number }) => {
    const payload = isGlobalEnabled
        ? {
              global: {
                  status: 'enabled',
                  volume: -1,
              },
          }
        : {
              [SoundTypes.BASE_GAME_BACKGROUND]: {
                  status: 'enabled',
                  volume: volumes?.[SoundTypes.BASE_GAME_BACKGROUND] ?? -1,
              },
              [SoundTypes.FEATURE_BACKGROUND]: {
                  status: 'enabled',
                  volume: volumes?.[SoundTypes.FEATURE_BACKGROUND] ?? -1,
              },
              global: {
                  status: 'disabled',
              },
          }

    window.uiBus.postMessage({
        type: GameUIMessageType.SOUND_CONFIG_CHANGE_REQUESTED,
        payload,
    })
}

const isActive = (status: string) => status === 'PLAYING' || status === 'PAUSED'

// Plays the Ultra Play sounds that are driven by session state (new result rows, wins, free spins,
// round end) by reacting to store transitions — keeping the spin recorder itself free of UI/sound
// side-effects. Button-press sounds live in the controls handlers instead. Mount once while open.
export const useUltraPlaySoundEffects = () => {
    useEffect(() => {
        const snapshot = (s: ReturnType<typeof useUltraPlayStore.getState>) => ({
            status: s.status,
            spinsLength: s.spins.length,
            topWin: s.spins[0]?.win ?? 0,
            topMultiplier: s.spins[0]?.winMultiplier ?? 0,
            isInFeature: s.isInFeature,
        })

        let prev = snapshot(useUltraPlayStore.getState())

        const unsubscribe = useUltraPlayStore.subscribe((state) => {
            const cur = snapshot(state)

            // A new base result row was added (free-spin sub-rows don't change spins.length).
            if (cur.spinsLength > prev.spinsLength) {
                playUltraPlaySound(
                    cur.topWin > 0 ? winSoundForMultiplier(cur.topMultiplier) : ULTRA_PLAY_SOUND.RESULT_ROW_APPEAR
                )
            }

            // Free spins feature boundaries.
            if (cur.isInFeature && !prev.isInFeature) playUltraPlaySound(ULTRA_PLAY_SOUND.ENTERING_FREE_SPINS)

            if (!cur.isInFeature && prev.isInFeature) playUltraPlaySound(ULTRA_PLAY_SOUND.FREE_SPINS_END)

            // Round ended naturally (active → READY with the table preserved). A user close resets the
            // store (spins cleared), so that path is excluded.
            if (isActive(prev.status) && cur.status === 'READY' && cur.spinsLength > 0) {
                playUltraPlaySound(ULTRA_PLAY_SOUND.END_ROUND)
            }

            // Restrict / restore engine sounds as the session becomes active / inactive.
            if (!isActive(prev.status) && isActive(cur.status))
                requestSoundConfigChange(false, {
                    [SoundTypes.BASE_GAME_BACKGROUND]: 0.22,
                    [SoundTypes.FEATURE_BACKGROUND]: 0.18,
                })

            if (isActive(prev.status) && !isActive(cur.status)) requestSoundConfigChange(true)

            prev = cur
        })

        return () => {
            unsubscribe()
            // Always restore engine sounds when Ultra Play closes.
            requestSoundConfigChange(true)
        }
    }, [])
}

// Null-rendering wrapper so the effect is scoped to the modal being open (UltraPlayModal renders
// this only while open, so it mounts/unmounts — and restores engine sounds — with the feature).
export const UltraPlaySoundEffects = (): null => {
    useUltraPlaySoundEffects()

    return null
}
